import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, Pencil, Trash2, GripVertical, Image as ImageIcon, Eye, EyeOff, Monitor, Smartphone, Link2 } from 'lucide-react'
import { bannersService } from '@/services/bannersService'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import MediaPickerModal from '@/components/shared/MediaPickerModal'
import { getLocalizedField } from '@/utils/formatters'
import { imgUrl } from '@/utils/img'
import { useTranslation } from 'react-i18next'

const EMPTY_FORM = {
  titleAr: '',
  titleEn: '',
  descAr: '',
  descEn: '',
  imageUrl: '',
  imagePublicId: '',
  linkUrl: '',
  showButton: true,
  buttonTextAr: '',
  buttonTextEn: '',
  buttonStyle: 'primary',
  textAlign: 'right',
  order: 0,
  isActive: true,
  startDate: '',
  endDate: '',
}

function BannerPreview({ form, lang, t }) {
  const [device, setDevice] = useState('desktop')
  const title = lang === 'ar' ? form.titleAr : form.titleEn
  const desc = lang === 'ar' ? form.descAr : form.descEn
  const defaultBtnText = lang === 'ar' ? 'تسوق الآن' : 'Shop now'
  const buttonText = (lang === 'ar' ? form.buttonTextAr : form.buttonTextEn) || defaultBtnText

  const justify = form.textAlign === 'center' ? 'center' : form.textAlign === 'left' ? 'flex-start' : 'flex-end'
  const textAlign = form.textAlign === 'center' ? 'center' : form.textAlign

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold" style={{ color: '#888', fontFamily: 'Tajawal, sans-serif' }}>
          {t('banners.preview', 'معاينة حية')}
        </span>
        <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid #E8ECF0' }}>
          <button
            type="button"
            onClick={() => setDevice('desktop')}
            className="px-2.5 py-1.5 transition-colors"
            style={{ background: device === 'desktop' ? 'rgba(0,6,102,0.08)' : '#fff', color: device === 'desktop' ? '#000666' : '#999' }}
          >
            <Monitor size={14} />
          </button>
          <button
            type="button"
            onClick={() => setDevice('mobile')}
            className="px-2.5 py-1.5 transition-colors"
            style={{ background: device === 'mobile' ? 'rgba(0,6,102,0.08)' : '#fff', color: device === 'mobile' ? '#000666' : '#999' }}
          >
            <Smartphone size={14} />
          </button>
        </div>
      </div>

      <div className="flex justify-center">
        <div
          className="relative overflow-hidden transition-all"
          style={{
            width: device === 'desktop' ? '100%' : 220,
            aspectRatio: device === 'desktop' ? '16 / 9' : '9 / 16',
            borderRadius: 16,
            background: form.imageUrl
              ? `url(${imgUrl(form.imageUrl)}) center/cover`
              : 'linear-gradient(135deg, #000666 0%, #1A237E 60%, #FF6D00 130%)',
            cursor: form.linkUrl ? 'pointer' : 'default',
          }}
        >
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(0,0,0,0.55), rgba(0,0,0,0) 65%)' }} />

          {/* Whole-banner link indicator — matches the storefront: the entire
              banner is clickable when a link is set, not just the button. */}
          {form.linkUrl && (
            <div
              className="absolute top-2.5 right-2.5 flex items-center gap-1 px-2 py-1 rounded-full"
              style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
            >
              <Link2 size={11} color="#fff" />
              <span style={{ color: '#fff', fontSize: 9, fontFamily: 'Tajawal, sans-serif' }}>
                {t('banners.wholeBannerClickable', 'البانر كله قابل للنقر')}
              </span>
            </div>
          )}

          <div className="relative h-full flex items-center px-5" style={{ justifyContent: justify }}>
            <div style={{ maxWidth: '75%', textAlign }}>
              <p className="font-black text-white mb-1" style={{ fontFamily: 'Cairo, sans-serif', fontSize: device === 'desktop' ? 18 : 14 }}>
                {title || '—'}
              </p>
              {desc && (
                <p className="mb-3" style={{ color: 'rgba(255,255,255,0.8)', fontFamily: 'Tajawal, sans-serif', fontSize: device === 'desktop' ? 12 : 10 }}>
                  {desc}
                </p>
              )}
              {form.showButton && (
                <span
                  className="inline-flex items-center gap-1.5 font-bold"
                  style={{
                    padding: device === 'desktop' ? '8px 16px' : '6px 12px',
                    borderRadius: 10,
                    fontSize: device === 'desktop' ? 12 : 10,
                    fontFamily: 'Cairo, sans-serif',
                    background: form.buttonStyle === 'outline' ? 'transparent' : '#FF6D00',
                    color: '#fff',
                    border: `1.5px solid ${form.buttonStyle === 'outline' ? '#fff' : '#FF6D00'}`,
                  }}
                >
                  {buttonText}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {form.showButton && !form.linkUrl && (
        <p className="text-xs mt-2" style={{ color: '#BA1A1A', fontFamily: 'Tajawal, sans-serif' }}>
          {t('banners.linkRequiredWarn', 'أدخل رابط الزر — الزر مش هيظهر من غيره.')}
        </p>
      )}
      {!form.showButton && !form.linkUrl && (
        <p className="text-xs mt-2" style={{ color: '#999', fontFamily: 'Tajawal, sans-serif' }}>
          {t('banners.noLinkHint', 'من غير رابط، البانر هيتعرض بس من غير ما يتنقل لأي مكان لو المستخدم ضغط عليه.')}
        </p>
      )}
    </div>
  )
}

export default function BannersPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language?.startsWith('ar') ? 'ar' : 'en'
  const qc = useQueryClient()

  const [dialog, setDialog] = useState(null) // null | 'create' | 'edit'
  const [form, setForm] = useState(EMPTY_FORM)
  const [editingId, setEditingId] = useState(null)
  const [deleteDialog, setDeleteDialog] = useState(null)
  const [mediaOpen, setMediaOpen] = useState(false)

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }))

  /* ── Queries ── */
  const { data, isLoading, isError } = useQuery({
    queryKey: ['banners'],
    queryFn: () => bannersService.getAll(),
  })

  const banners = data?.data || []

  /* ── Mutations ── */
  const createMut = useMutation({
    mutationFn: bannersService.create,
    onSuccess: () => {
      toast.success(t('banners.created', 'تم إنشاء الـ banner بنجاح'))
      qc.invalidateQueries({ queryKey: ['banners'] })
      closeDialog()
    },
    onError: (e) => toast.error(e?.response?.data?.message || t('banners.error', 'حدث خطأ')),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => bannersService.update(id, data),
    onSuccess: () => {
      toast.success(t('banners.updated', 'تم تحديث الـ banner بنجاح'))
      qc.invalidateQueries({ queryKey: ['banners'] })
      closeDialog()
    },
    onError: (e) => toast.error(e?.response?.data?.message || t('banners.error', 'حدث خطأ')),
  })

  const deleteMut = useMutation({
    mutationFn: bannersService.delete,
    onSuccess: () => {
      toast.success(t('banners.deleted', 'تم حذف الـ banner بنجاح'))
      qc.invalidateQueries({ queryKey: ['banners'] })
      setDeleteDialog(null)
    },
    onError: (e) => toast.error(e?.response?.data?.message || t('banners.error', 'حدث خطأ')),
  })

  const toggleMut = useMutation({
    mutationFn: bannersService.toggle,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['banners'] })
    },
  })

  /* ── Handlers ── */
  function openCreate() {
    setForm(EMPTY_FORM)
    setEditingId(null)
    setDialog('create')
  }

  function openEdit(b) {
    setForm({
      titleAr: getLocalizedField(b.title, 'ar'),
      titleEn: getLocalizedField(b.title, 'en'),
      descAr: getLocalizedField(b.description, 'ar'),
      descEn: getLocalizedField(b.description, 'en'),
      imageUrl: b.imageUrl || '',
      imagePublicId: b.imagePublicId || '',
      linkUrl: b.linkUrl || '',
      showButton: b.showButton ?? true,
      buttonTextAr: getLocalizedField(b.buttonText, 'ar'),
      buttonTextEn: getLocalizedField(b.buttonText, 'en'),
      buttonStyle: b.buttonStyle || 'primary',
      textAlign: b.textAlign || 'right',
      order: b.order || 0,
      isActive: b.isActive ?? true,
      startDate: b.startDate ? b.startDate.split('T')[0] : '',
      endDate: b.endDate ? b.endDate.split('T')[0] : '',
    })
    setEditingId(b.id)
    setDialog('edit')
  }

  function closeDialog() {
    setDialog(null)
    setEditingId(null)
    setForm(EMPTY_FORM)
  }

  function toISOString(dateStr) {
    if (!dateStr) return null
    return new Date(dateStr + 'T00:00:00.000Z').toISOString()
  }

  function handleSubmit(e) {
    e.preventDefault()
    const payload = {
      title: { ar: form.titleAr, en: form.titleEn },
      description: { ar: form.descAr, en: form.descEn },
      imageUrl: form.imageUrl,
      imagePublicId: form.imagePublicId,
      linkUrl: form.linkUrl || null,
      showButton: form.showButton,
      buttonText: { ar: form.buttonTextAr, en: form.buttonTextEn },
      buttonStyle: form.buttonStyle,
      textAlign: form.textAlign,
      order: Number(form.order),
      isActive: form.isActive,
      startDate: toISOString(form.startDate),
      endDate: toISOString(form.endDate),
    }

    if (dialog === 'create') {
      createMut.mutate(payload)
    } else {
      updateMut.mutate({ id: editingId, data: payload })
    }
  }

  /* ── Loading / Error ── */
  if (isLoading) return <LoadingSpinner fullPage />
  if (isError) return <EmptyState message={t('common.error', 'حدث خطأ')} />

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Cairo, sans-serif', color: '#000666' }}>
            {t('nav.banners', 'البانرات')}
          </h2>
          <p className="text-sm mt-1" style={{ color: '#666', fontFamily: 'Tajawal, sans-serif' }}>
            {t('banners.subtitle', 'إدارة بانرات الصفحة الرئيسية')}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold transition-all hover:shadow-lg active:scale-95"
          style={{ background: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}
        >
          <Plus size={18} />
          {t('banners.add', 'إضافة بانر')}
        </button>
      </div>

      {/* Table */}
      {banners.length === 0 ? (
        <EmptyState
          icon={ImageIcon}
          message={t('banners.empty', 'لا يوجد بانرات بعد')}
          action={
            <button
              onClick={openCreate}
              className="px-4 py-2 rounded-xl text-white text-sm font-bold transition-all hover:shadow-lg active:scale-95"
              style={{ background: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}
            >
              {t('banners.add', 'إضافة بانر')}
            </button>
          }
        />
      ) : (
        <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <table className="w-full text-right" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            <thead>
              <tr style={{ background: '#F5F7FA', borderBottom: '1px solid #E8ECF0' }}>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: '#888' }}>{t('banners.order', 'الترتيب')}</th>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: '#888' }}>{t('banners.image', 'الصورة')}</th>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: '#888' }}>{t('banners.title', 'العنوان')}</th>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: '#888' }}>{t('banners.link', 'الرابط')}</th>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: '#888' }}>{t('banners.status', 'الحالة')}</th>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: '#888' }}>{t('banners.dates', 'التواريخ')}</th>
                <th className="px-4 py-3 text-xs font-bold" style={{ color: '#888' }}>{t('banners.actions', 'إجراءات')}</th>
              </tr>
            </thead>
            <tbody>
              {banners.map((b) => (
                <tr key={b.id} className="transition-colors hover:bg-gray-50" style={{ borderBottom: '1px solid #F0F0F0' }}>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-sm font-bold" style={{ color: '#000666' }}>
                      <GripVertical size={14} style={{ color: '#CCC' }} />
                      {b.order}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <img
                      src={imgUrl(b.imageUrl)}
                      alt={getLocalizedField(b.title, 'ar')}
                      className="w-20 h-12 object-cover rounded-lg"
                      style={{ border: '1px solid #E8ECF0' }}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-bold" style={{ color: '#000666' }}>
                      {getLocalizedField(b.title, lang)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {b.linkUrl ? (
                      <span className="text-xs truncate block max-w-[150px]" style={{ color: '#FF6D00' }}>
                        {b.linkUrl}
                      </span>
                    ) : (
                      <span className="text-xs" style={{ color: '#CCC' }}>—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleMut.mutate(b.id)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition-all hover:scale-105"
                      style={{
                        background: b.isActive ? '#E8F5E9' : '#FFF3E0',
                        color: b.isActive ? '#1B6D24' : '#FF6D00',
                      }}
                    >
                      {b.isActive ? <Eye size={12} /> : <EyeOff size={12} />}
                      {b.isActive ? t('banners.active', 'نشط') : t('banners.inactive', 'غير نشط')}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs" style={{ color: '#888' }}>
                      {b.startDate ? new Date(b.startDate).toLocaleDateString('ar-EG') : '—'}
                      {b.endDate ? ` → ${new Date(b.endDate).toLocaleDateString('ar-EG')}` : ''}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(b)}
                        className="p-1.5 rounded-lg transition-colors hover:bg-blue-50"
                        style={{ color: '#000666' }}
                        title={t('common.edit', 'تعديل')}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => setDeleteDialog(b)}
                        className="p-1.5 rounded-lg transition-colors hover:bg-red-50"
                        style={{ color: '#BA1A1A' }}
                        title={t('common.delete', 'حذف')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Dialog ── */}
      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-5xl max-h-[90vh] rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_320px]" style={{ background: '#fff', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
          <div className="p-6 overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-bold mb-5" style={{ fontFamily: 'Cairo, sans-serif', color: '#000666' }}>
              {dialog === 'create' ? t('banners.add', 'إضافة بانر') : t('banners.edit', 'تعديل الـ banner')}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title AR */}
              <div>
                <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                  {t('banners.titleAr', 'العنوان (عربي)')}
                </label>
                <input
                  type="text"
                  value={form.titleAr}
                  onChange={(e) => set('titleAr', e.target.value)}
                  className="hg-input w-full"
                  dir="rtl"
                />
              </div>

              {/* Title EN */}
              <div>
                <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                  {t('banners.titleEn', 'العنوان (إنجليزي)')}
                </label>
                <input
                  type="text"
                  value={form.titleEn}
                  onChange={(e) => set('titleEn', e.target.value)}
                  className="hg-input w-full"
                  dir="ltr"
                />
              </div>

              {/* Description AR */}
              <div>
                <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                  {t('banners.descAr', 'الوصف (عربي)')}
                </label>
                <textarea
                  value={form.descAr}
                  onChange={(e) => set('descAr', e.target.value)}
                  className="hg-input w-full"
                  rows={2}
                  dir="rtl"
                />
              </div>

              {/* Description EN */}
              <div>
                <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                  {t('banners.descEn', 'الوصف (إنجليزي)')}
                </label>
                <textarea
                  value={form.descEn}
                  onChange={(e) => set('descEn', e.target.value)}
                  className="hg-input w-full"
                  rows={2}
                  dir="ltr"
                />
              </div>

              {/* Image */}
              <div>
                <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                  {t('banners.image', 'الصورة')}
                </label>
                <div className="flex items-center gap-3">
                  {form.imageUrl ? (
                    <img src={imgUrl(form.imageUrl)} alt="" className="w-24 h-16 object-cover rounded-lg" style={{ border: '1px solid #E8ECF0' }} />
                  ) : (
                    <div className="w-24 h-16 rounded-lg flex items-center justify-center" style={{ background: '#F5F5F5', border: '2px dashed #DDD' }}>
                      <ImageIcon size={20} style={{ color: '#CCC' }} />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setMediaOpen(true)}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold transition-colors hover:bg-gray-100"
                      style={{ border: '1px solid #DDD', fontFamily: 'Tajawal, sans-serif' }}
                    >
                      {t('banners.choose', 'اختيار صورة')}
                    </button>
                    {form.imageUrl && (
                      <button
                        type="button"
                        onClick={() => { set('imageUrl', ''); set('imagePublicId', '') }}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold transition-colors hover:bg-red-50"
                        style={{ color: '#BA1A1A', border: '1px solid #FFCDD2', fontFamily: 'Tajawal, sans-serif' }}
                      >
                        {t('common.remove', 'إزالة')}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Link URL */}
              <div>
                <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                  {t('banners.link', 'رابط البانر (اختياري)')}
                </label>
                <input
                  type="text"
                  value={form.linkUrl}
                  onChange={(e) => set('linkUrl', e.target.value)}
                  className="hg-input w-full"
                  placeholder="https://... أو /products/..."
                  dir="ltr"
                />
                <p className="text-xs mt-1" style={{ color: '#999', fontFamily: 'Tajawal, sans-serif' }}>
                  {t('banners.linkHint', 'لو اتحط رابط هنا، البانر كله هيبقى قابل للنقر وهيودّي عليه — مش بس الزرار.')}
                </p>
              </div>

              {/* Button customization */}
              <div className="rounded-xl p-4" style={{ background: '#FAFAFA', border: '1px solid #F0F0F0' }}>
                <label className="flex items-center justify-between cursor-pointer mb-3">
                  <span className="text-sm font-bold" style={{ color: '#000666', fontFamily: 'Cairo, sans-serif' }}>
                    {t('banners.showButton', 'إظهار الزر على البانر')}
                  </span>
                  <input
                    type="checkbox"
                    checked={form.showButton}
                    onChange={(e) => set('showButton', e.target.checked)}
                    className="w-4 h-4 rounded accent-[#FF6D00]"
                  />
                </label>

                {form.showButton && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                          {t('banners.buttonTextAr', 'نص الزر (عربي)')}
                        </label>
                        <input
                          type="text"
                          value={form.buttonTextAr}
                          onChange={(e) => set('buttonTextAr', e.target.value)}
                          className="hg-input w-full"
                          placeholder="تسوق الآن"
                          dir="rtl"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                          {t('banners.buttonTextEn', 'نص الزر (إنجليزي)')}
                        </label>
                        <input
                          type="text"
                          value={form.buttonTextEn}
                          onChange={(e) => set('buttonTextEn', e.target.value)}
                          className="hg-input w-full"
                          placeholder="Shop now"
                          dir="ltr"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                          {t('banners.buttonStyle', 'شكل الزر')}
                        </label>
                        <select
                          value={form.buttonStyle}
                          onChange={(e) => set('buttonStyle', e.target.value)}
                          className="hg-input w-full"
                        >
                          <option value="primary">{t('banners.styleFilled', 'معبأ')}</option>
                          <option value="outline">{t('banners.styleOutline', 'مفرغ (outline)')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                          {t('banners.textAlign', 'محاذاة النص')}
                        </label>
                        <select
                          value={form.textAlign}
                          onChange={(e) => set('textAlign', e.target.value)}
                          className="hg-input w-full"
                        >
                          <option value="right">{t('banners.alignRight', 'يمين')}</option>
                          <option value="center">{t('banners.alignCenter', 'وسط')}</option>
                          <option value="left">{t('banners.alignLeft', 'يسار')}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Order + Active */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                    {t('banners.order', 'الترتيب')}
                  </label>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => set('order', e.target.value)}
                    className="hg-input w-full"
                    min={0}
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => set('isActive', e.target.checked)}
                      className="w-4 h-4 rounded accent-[#FF6D00]"
                    />
                    <span className="text-sm font-bold" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                      {t('banners.active', 'نشط')}
                    </span>
                  </label>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                    {t('banners.startDate', 'تاريخ البداية')}
                  </label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => set('startDate', e.target.value)}
                    className="hg-input w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1.5" style={{ color: '#555', fontFamily: 'Tajawal, sans-serif' }}>
                    {t('banners.endDate', 'تاريخ النهاية')}
                  </label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => set('endDate', e.target.value)}
                    className="hg-input w-full"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-3" style={{ borderTop: '1px solid #F0F0F0' }}>
                <button
                  type="button"
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-xl text-sm font-bold transition-colors hover:bg-gray-100"
                  style={{ fontFamily: 'Tajawal, sans-serif' }}
                >
                  {t('common.cancel', 'إلغاء')}
                </button>
                <button
                  type="submit"
                  disabled={createMut.isPending || updateMut.isPending}
                  className="px-5 py-2 rounded-xl text-white text-sm font-bold transition-all hover:shadow-lg active:scale-95 disabled:opacity-50"
                  style={{ background: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}
                >
                  {dialog === 'create' ? t('banners.createBtn', 'إنشاء') : t('banners.save', 'حفظ')}
                </button>
              </div>
            </form>
          </div>

          <div className="p-6 overflow-y-auto max-h-[90vh]" style={{ background: '#FAFAFA', borderInlineStart: '1px solid #F0F0F0' }}>
            <BannerPreview form={form} lang={lang} t={t} />
          </div>
          </div>
        </div>
      )}

      {/* ── Delete Dialog ── */}
      {deleteDialog && (
        <ConfirmDialog
          title={t('banners.deleteTitle', 'حذف الـ banner')}
          message={t('banners.deleteMsg', `هل أنت متأكد من حذف "${getLocalizedField(deleteDialog.title, lang)}"؟`)}
          confirmLabel={t('common.delete', 'حذف')}
          cancelLabel={t('common.cancel', 'إلغاء')}
          onConfirm={() => deleteMut.mutate(deleteDialog.id)}
          onCancel={() => setDeleteDialog(null)}
          loading={deleteMut.isPending}
        />
      )}

      {/* ── Media Picker ── */}
      {mediaOpen && (
        <MediaPickerModal
          open={mediaOpen}
          onClose={() => setMediaOpen(false)}
          onSelect={({ url, publicId }) => {
            set('imageUrl', url)
            set('imagePublicId', publicId)
            setMediaOpen(false)
          }}
          title={t('banners.chooseImage', 'اختيار صورة الـ banner')}
          folder="banners"
        />
      )}
    </div>
  )
}
